import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { signaling } from '@lifesize/clients.sdk';
import { colors } from '@lifesize/ux-assets';
import { ReactComponent as MicIcon } from '@lifesize/ux-assets/vectors/svgIcons/mic.svg';
import { ReactComponent as MicOffIcon } from '@lifesize/ux-assets/vectors/svgIcons/mic-off.svg';
import { makeStyles, Theme, Tooltip } from '@material-ui/core';
import { fade } from '@material-ui/core/styles';
import { setPillNotificationWithTimeout } from 'actions/Notification';

interface Props {
  intl: { formatMessage: Function };
  isMuteAll: boolean;
}

const messages = defineMessages({
  muteAll: {
    id: 'components.incall.footer.notification.muteall',
    defaultMessage: 'All participants are now muted. They cannot unmute themselves.'
  },
  unMuteAll: {
    id: 'components.incall.footer.notification.unMuteAll',
    defaultMessage: 'All participants are now unmuted, unless they have muted themselves.'
  },
  muteAllTooltip: {
    id: 'contacts.tooltip.muteAll',
    defaultMessage: 'Participants muted by the moderator cannot unmute themselves.'
  },
  unMuteAllTooltip: {
    id: 'contacts.tooltip.unmuteAll',
    defaultMessage: 'Unmute participants. Self-muted participants will remain muted.'
  }
});

export const UnMuteAllButton = ({ isMuteAll, intl }: Props) => {
  const classes = useStyles({});
  const tooltipClasses = useStylesTooltip();
  const dispatch = useDispatch();

  const getTooltipMessage = useCallback(
    (isMuteAll: boolean) => {
      if (isMuteAll) {
        return intl.formatMessage(messages.muteAllTooltip);
      }
      return intl.formatMessage(messages.unMuteAllTooltip);
    },
    [intl]
  );

  const handleMuteAllClicked = (mute: boolean) => {
    signaling.moderator.setAllParticipantsMuteValue(mute).then(() => {
      dispatch(setPillNotificationWithTimeout(intl.formatMessage(mute ? messages.muteAll : messages.unMuteAll)));
    });
  };

  const ButtonIcon = isMuteAll ? MicOffIcon : MicIcon;

  return (
    <Tooltip title={getTooltipMessage(isMuteAll)} classes={tooltipClasses} placement="top" arrow={true}>
      <button
        className={classes.button}
        data-testid={isMuteAll ? 'btnMuteAll' : 'btnUnmuteAll'}
        key={isMuteAll ? 'muteAll' : 'unmuteAll'}
        onClick={() => {
          handleMuteAllClicked(isMuteAll);
        }}
      >
        <ButtonIcon />
        <span>
          {isMuteAll ? (
            <FormattedMessage id="components.inCall.mute" defaultMessage="Mute All" />
          ) : (
            <FormattedMessage id="components.inCall.unMute" defaultMessage="Unmute All" />
          )}
        </span>
      </button>
    </Tooltip>
  );
};

const useStylesTooltip = makeStyles(() => ({
  popper: {
    maxWidth: 245,
    textAlign: 'center',
    '& div': {
      marginBottom: 8
    }
  }
}));

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    alignItems: 'center',
    borderWidth: '1px 0 0!important',
    borderColor: `${colors.sentinel}!important`,
    borderStyle: 'solid!important',
    border: 0,
    display: 'flex',
    flexDirection: 'row',
    fontSize: '1em',
    justifyContent: 'flex-start',
    minHeight: 38,
    outline: 0,
    padding: `${theme.spacing(0.75)}px ${theme.spacing(3)}px`,
    position: 'relative',
    width: '100%',

    '& svg': {
      color: colors.lifesizeBlue,
      flex: '0 0 auto',
      height: '2.6em',
      marginRight: theme.spacing(2)
    },
    '& > span': {
      color: colors.squidInk,
      fontSize: '1.4em',
      fontWeight: theme.typography.fontWeightBold,
      textAlign: 'left'
    },
    '&:hover:not(:disabled)': {
      backgroundColor: fade(colors.lifesizeBlue, 0.2)
    },
    '&:disabled': {
      cursor: 'default',
      '& svg': {
        color: fade(colors.lifesizeBlue, 0.5)
      },
      '& > span': {
        color: fade(colors.squidInk, 0.5)
      }
    }
  }
}));

export default injectIntl(UnMuteAllButton);
